import { GridColDef } from "@mui/x-data-grid";
import { Menuitem } from "../../interfaces/Security/menu";
import { useMenuStore } from "../../store/AuthStore";
import { useGridColumnOrderPreferencesStore, useGridColumnSortPreferencesStore, useGridColumnVisiblePreferencesStore, useGridFilterPreferencesStore } from "../../store/Preferences/PreferencesStore";
import { ColumnsGridMUI } from "./helpers/ColumnsGridMUI";
import { useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import GenericPromises from "../../api/GenericPromises";
import { GridFilterPreference, GridOrderPreference, GridSortPreference, GridVisiblePreference, PreferenceOrderValue } from "../../interfaces/Commons/Preferences/gridPreferences";
import { FilterColumnsGridMUI } from "./helpers/FilterColumnsGridMUI";

export const useGridPreferences = () => {
  const { gridColumnOrderPreferences, addColumnOrderPreference } = useGridColumnOrderPreferencesStore.getState();
  const { gridColumnVisiblePreferences, addColumnVisiblePreference } = useGridColumnVisiblePreferencesStore.getState();
  const { gridColumnFilterPreferences, addColumnFilterPreference } = useGridFilterPreferencesStore.getState();
  const { gridColumnSortPreferences, addColumnSortPreference } = useGridColumnSortPreferencesStore.getState();
  const { menuState } = useMenuStore();
  const { GetColumnDefinitionByColumnsOrder, GetColumnDefinitionByColumnsVisible, GetColumnDefinitionByColumnsOrderProjects, GetColumnDefinitionByColumnsSort } = ColumnsGridMUI();
  const { GetFilterDefinitionByFilter } = FilterColumnsGridMUI();
  const { user } = useContext(AuthContext);
  const { GenericGetResource } = GenericPromises();

  function findResourceIdByRoute(menuOptions: Menuitem | undefined, resourceRoute: string): number | null {
    if (!menuOptions) return null;
    for (let i = 0; i < menuOptions.children.length; i++) {
      const option = menuOptions.children[i];
      if (option.resource_route === resourceRoute) {
        return option.resource_id;
      }
      if (option.children && option.children.length > 0) {
        const result = findResourceIdByRoute(option, resourceRoute);
        if (result !== null) {
          return result;
        }
      }
    }
    return null;
  }

  const LoadVisualPreferences = async (entity: string) => {
    let myPreferences = await localStorage.getItem(entity);
    if (myPreferences != null) {
      return JSON.parse(myPreferences);
    }
  }

  const LoadColumnsOrder = async (entity: string, customColumnsData: GridColDef<any>[] = [], preferenceValue: PreferenceOrderValue[] | string | undefined, firstCustomColumn: boolean = false): Promise<GridColDef<any>[]> => {
    if (typeof (preferenceValue) === 'string') {
      return await GetColumnDefinitionByColumnsOrder(JSON.parse(preferenceValue ?? ''), entity.substring(1), customColumnsData, firstCustomColumn);
    }
    else {
      return await GetColumnDefinitionByColumnsOrder(preferenceValue, entity.substring(1), customColumnsData, firstCustomColumn);
    }
  }

  const LoadColumnsOrderProjects = async (entity: string, customColumnsData: GridColDef<any>[] = [], preferenceValue: PreferenceOrderValue[] | string | undefined, projectExtrafieldsColumns: GridColDef<any>[] = []): Promise<GridColDef<any>[]> => {
    if (typeof (preferenceValue) === 'string') {
      return await GetColumnDefinitionByColumnsOrderProjects(JSON.parse(preferenceValue ?? ''), entity.substring(1), customColumnsData, projectExtrafieldsColumns);
    }
    else {
      return await GetColumnDefinitionByColumnsOrderProjects(preferenceValue, entity.substring(1), customColumnsData, projectExtrafieldsColumns);
    }
  }

  const LoadColumnsVisible = async (entity: string, preference: GridVisiblePreference) => {
    if (typeof (preference.preference_value) === 'string') {
      return await GetColumnDefinitionByColumnsVisible(JSON.parse(preference.preference_value ?? []), (preference.preference_id !== undefined && preference.preference_id > 0) ? true : false);
    }
    else {
      return await GetColumnDefinitionByColumnsVisible(preference.preference_value ?? [], (preference.preference_id !== undefined && preference.preference_id > 0) ? true : false);
    }
  }

  const LoadFilter = async (entity: string, preference: GridFilterPreference) => {
    if (typeof (preference.preference_value) === 'string') {
      return await GetFilterDefinitionByFilter(JSON.parse(preference.preference_value ?? []), (preference.preference_id !== undefined && preference.preference_id > 0) ? true : false);
    }
    else {
      return await GetFilterDefinitionByFilter(preference.preference_value ?? [], (preference.preference_id !== undefined && preference.preference_id > 0) ? true : false);
    }
  }

  const LoadColumnsSort = async (entity: string, preference: GridSortPreference) => {
    if (typeof (preference.preference_value) === 'string') {
      return await GetColumnDefinitionByColumnsSort(JSON.parse(preference.preference_value ?? []), (preference.preference_id !== undefined && preference.preference_id > 0) ? true : false);
    }
    else {
      return await GetColumnDefinitionByColumnsSort(preference.preference_value ?? [], (preference.preference_id !== undefined && preference.preference_id > 0) ? true : false);
    }
  }

  const VerifyPreferenceOrderByResourceRoute = async (entity: string, preferenceTypeId: number): Promise<GridOrderPreference | undefined> => {
    // verify in store if exists 
    try {
      let myResourceId = findResourceIdByRoute(menuState, entity);
      if (myResourceId === null) return undefined;
      let myColumnsOrder = await gridColumnOrderPreferences.find((preference) => preference.resource_id === myResourceId);
      if (myColumnsOrder) {
        return myColumnsOrder;
      }
      else if (!myColumnsOrder) {
        // get from api and put into store
        const responsePreference = await GenericGetResource(`/preferences/grid/bypreferencetypeid/${preferenceTypeId}/byusercompanyid/${user?.user_company_id}/byresourceid/${myResourceId}`);
        const newPreference = responsePreference.data[0];
        if (!newPreference) return undefined;

        addColumnOrderPreference(newPreference);
        return newPreference;
      }
    }
    catch (error: any) {
      throw error;
    }
  }

  const VerifyPreferenceVisualByResourceRoute = async (entity: string, preferenceTypeId: number): Promise<GridVisiblePreference | undefined> => {
    // verify in store if exists return or get from api and put into store
    let myResourceId = findResourceIdByRoute(menuState, entity);
    if (myResourceId === null) return undefined;
    let myColumnsVisible = await gridColumnVisiblePreferences.find((preference) => preference.resource_id === myResourceId);
    if (myColumnsVisible) {
      return myColumnsVisible;
    }
    else if (!myColumnsVisible) {
      const responsePreference = await GenericGetResource(`/preferences/grid/bypreferencetypeid/${preferenceTypeId}/byusercompanyid/${user?.user_company_id}/byresourceid/${myResourceId}`);
      const newPreference = responsePreference.data[0];
      if (!newPreference) return undefined;

      addColumnVisiblePreference(newPreference);
      return newPreference;
    }
  }

  const VerifyPreferenceFilterByResourceRoute = async (entity: string, preferenceTypeId: number): Promise<GridFilterPreference | undefined> => {
    // verify in store if exists return or get from api and put into store
    let myResourceId = findResourceIdByRoute(menuState, entity);
    if (myResourceId === null) return undefined;
    let myColumnsFilter = await gridColumnFilterPreferences.find((preference) => preference.resource_id === myResourceId);
    if (myColumnsFilter) {
      return myColumnsFilter;
    }
    else if (!myColumnsFilter) {
      const responsePreference = await GenericGetResource(`/preferences/grid/bypreferencetypeid/${preferenceTypeId}/byusercompanyid/${user?.user_company_id}/byresourceid/${myResourceId}`);
      const newPreference = responsePreference.data[0];
      if (!newPreference) return undefined;

      addColumnFilterPreference(newPreference);
      return newPreference;
    }
  }

  const VerifyPreferenceSortByResourceRoute = async (entity: string, preferenceTypeId: number): Promise<GridSortPreference | undefined> => {
    // verify in store if exists return or get from api and put into store
    let myResourceId = findResourceIdByRoute(menuState, entity);
    if (myResourceId === null) return undefined;
    let myColumnsSort = await gridColumnSortPreferences.find((preference) => preference.resource_id === myResourceId);
    if (myColumnsSort) {
      return myColumnsSort;
    }
    else if (!myColumnsSort) {
      const responsePreference = await GenericGetResource(`/preferences/grid/bypreferencetypeid/${preferenceTypeId}/byusercompanyid/${user?.user_company_id}/byresourceid/${myResourceId}`);
      const newPreference = responsePreference.data[0];
      if (!newPreference) return undefined;

      addColumnSortPreference(newPreference);
      return newPreference;
    }
  }

  return {
    LoadVisualPreferences,
    LoadColumnsOrder,
    LoadColumnsOrderProjects,
    LoadColumnsVisible,
    findResourceIdByRoute,
    VerifyPreferenceOrderByResourceRoute,
    VerifyPreferenceVisualByResourceRoute,
    VerifyPreferenceFilterByResourceRoute,
    VerifyPreferenceSortByResourceRoute,
    LoadFilter,
    LoadColumnsSort
  }
}
